import React from 'react';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import type { Project } from '../../types/project';
import type { ClosetCounters, ClosetState, Counter, JobStatus } from './types';

export const counterDefaults = (): Counter => ({ new: 0, pending: 0, failed: 0, total: 0 });

export const closetCounterDefaults = (): ClosetCounters => ({
  total: 0,
  done: 0,
  new: 0,
  pending: 0,
  failed: 0,
  unknown: 0,
});

export const closetStatusOrder: Record<ClosetState, number> = {
  failed: 0,
  pending: 1,
  new: 2,
  unknown: 3,
  done: 4,
};

export const jobStatusMeta: Record<
  JobStatus,
  { label: string; color: string; icon: React.ReactNode }
> = {
  new: { label: 'Queued', color: 'geekblue', icon: <RocketOutlined /> },
  pending: { label: 'Rendering', color: 'gold', icon: <ClockCircleOutlined /> },
  failed: { label: 'Failed', color: 'volcano', icon: <CloseCircleOutlined /> },
};

export const closetStatusMeta: Record<
  ClosetState,
  { label: string; color?: string; icon: React.ReactNode }
> = {
  new: { label: 'Queued', color: 'geekblue', icon: <RocketOutlined /> },
  pending: { label: 'Rendering', color: 'gold', icon: <ClockCircleOutlined /> },
  failed: { label: 'Failed', color: 'volcano', icon: <CloseCircleOutlined /> },
  done: { label: 'Done', color: '#52c41a', icon: <CheckCircleOutlined /> },
  unknown: { label: 'Unknown', color: '#bfbfbf', icon: <QuestionCircleOutlined /> },
};

export const normalizeClosetStatus = (status?: string | null): ClosetState => {
  if (!status) {
    return 'unknown';
  }
  const normalized = status.trim().toLowerCase();
  if (['done', 'completed', 'complete', 'success'].includes(normalized)) {
    return 'done';
  }
  if (['failed', 'error', 'errored'].includes(normalized)) {
    return 'failed';
  }
  if (
    ['pending', 'rendering', 'processing', 'in-progress', 'in progress', 'running'].includes(
      normalized,
    )
  ) {
    return 'pending';
  }
  if (['new', 'queued', 'queue'].includes(normalized)) {
    return 'new';
  }
  return 'unknown';
};

export const getProjectKey = (project: Project, index: number): string =>
  project.id || project.projectId || project.pk || `${project.name ?? 'project'}-${index}`;

export const buildDesignUrl = (
  projectId?: string,
  closetId?: string,
  automate?: boolean,
): string | undefined => {
  if (!projectId || !closetId) {
    return undefined;
  }
  const params = new URLSearchParams();
  params.set('project_id', projectId);
  params.set('closet_id', closetId);
  if (automate) {
    params.set('automate', 'true');
  }
  return `/design?${params.toString()}`;
};

export const formatRelativeTime = (dateString?: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'just now';
  }
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
  }
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  }
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  }
  return date.toLocaleDateString();
};

export const useRelativeTime = (dateString?: string, intervalMs = 60000): string => {
  const [relativeTime, setRelativeTime] = React.useState(formatRelativeTime(dateString));

  React.useEffect(() => {
    setRelativeTime(formatRelativeTime(dateString));
    const interval = setInterval(() => {
      setRelativeTime(formatRelativeTime(dateString));
    }, intervalMs);
    return () => clearInterval(interval);
  }, [dateString, intervalMs]);

  return relativeTime;
};

export const getProjectName = (email: string): string => {
  const now = new Date();

  const pad = (n: number): string => String(n).padStart(2, '0');

  let hours = now.getHours();
  const minutes = pad(now.getMinutes());

  const ampm = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12;
  hours = hours || 12;

  const formattedTime = `${now.getFullYear()}-${pad(
    now.getMonth() + 1,
  )}-${pad(now.getDate())} ${pad(hours)}:${minutes} ${ampm}`;

  return `${email}-${formattedTime}`;
};
